import ReactDOMServer from "react-dom/server";
import styled from "styled-components";

import { colorsPalettes, mixins, rgba } from "@similarweb/styles";

import { ClosableItemColorMarker } from "app/components/compare/StyledComponent";
import { i18nFilter } from "app/filters/i18nFilter";
import {
    numbersDataFormat,
    percentDataFormat,
    numbersFilter,
    percentsFilter,
} from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewBarChart/ChannelsOverviewBarChartContainerFormat";
import { FlexRow, FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";

export const TableCellStyle = styled.div`
    display: flex;
    align-items: center;

    &:nth-child(1) {
        margin-right: 10px;
    }
`;

export const TableRowStyle = styled(FlexRow)`
    font-weight: 400;
    font-style: normal;
    font-size: 14px;
    letter-spacing: 0.6px;
    padding: 4px 0;
    line-height: 24px;
    justify-content: space-between;
`;

export const TableCellDomainMarker = styled(ClosableItemColorMarker)<{ background: string }>`
    position: static;
    background-color: ${(p) => p.background};
    margin: 0 8px 0 0;
    flex-shrink: 0;
    width: 10px;
    height: 10px;
`;

export const TableCellDomainName = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 230px;
`;

export const TooltipContainer = styled(FlexColumn)`
    max-width: 350px;
    min-width: 260px;
    color: ${rgba(colorsPalettes.carbon[500], 0.8)};
`;

export const Title = styled.span`
    ${mixins.setFont({ $size: 16 })};
    font-weight: bold;
    margin-bottom: 10px;
`;

const AverageComponentWrapper = styled(TableRowStyle)`
    border-top: 1px solid ${colorsPalettes.carbon[50]};
    padding-top: 20px;
    margin-top: 20px;
`;

interface IPoint {
    point: { y: number; color: string };
    series: { color: string; name: string };
    x: { title: string };
}

interface IChannelsOverviewBarChartTooltipProps {
    points: IPoint[];
    title: string;
    dataFormat: string;
    isCompare: boolean;
    isBenchmarkMode: boolean;
}

export const ChannelsOverviewBarChartTooltip = ({
    points,
    title,
    dataFormat,
    isBenchmarkMode,
    isCompare,
}: IChannelsOverviewBarChartTooltipProps) => {
    const pointsList = points.map((point) => {
        return {
            traffic: point.point.y,
            color: point.point.color,
            name: point.series.name,
        };
    });

    const TableRow = ({ point }) => {
        const value = point.traffic
            ? dataFormat === percentDataFormat
                ? percentsFilter(point.traffic)
                : numbersFilter(point.traffic)
            : "N/A";
        return (
            <TableRowStyle>
                <TableCellStyle>
                    <TableCellDomainMarker background={point.color} />
                    <TableCellDomainName>{point.name}</TableCellDomainName>
                </TableCellStyle>
                <TableCellStyle>{value}</TableCellStyle>
            </TableRowStyle>
        );
    };

    const getAverageComponent = () => {
        const avg = pointsList.reduce((sum, item) => sum + item.traffic, 0) / pointsList.length;

        return (
            <AverageComponentWrapper>
                <TableCellStyle>
                    <TableCellDomainName>
                        {i18nFilter()("mmx.channelsoverview.bar.chart.tooltip.compare.average")}
                    </TableCellDomainName>
                </TableCellStyle>
                <TableCellStyle>{avg ? numbersFilter(avg) : "N/A"}</TableCellStyle>
            </AverageComponentWrapper>
        );
    };

    return ReactDOMServer.renderToString(
        <TooltipContainer>
            <Title>{title}</Title>
            {pointsList.map((pointData, index) => (
                <TableRow
                    point={pointData}
                    key={index}
                />
            ))}
            {isCompare && !isBenchmarkMode && dataFormat === numbersDataFormat ? getAverageComponent() : null}
        </TooltipContainer>,
    );
};
